import type { LaneId } from "@fate-kingdom/engine";

export type AlphaBattlefieldCameraMode =
  "overview" | "player" | "opponent" | "placement" | "attack" | "combat" | "inspect" | "result";

export type AlphaBattlefieldPanSnap = "opponent" | "overview" | "player";

export interface AlphaBattlefieldCameraState {
  readonly mode: AlphaBattlefieldCameraMode;
  /** Compatibility alias for the vertical presentation pan. */
  readonly pan: number;
  readonly panX: number;
  readonly panY: number;
  /** Presentation-only tabletop dolly. One is the default overview distance. */
  readonly zoom: number;
  readonly snap: AlphaBattlefieldPanSnap;
  readonly focusLaneId?: LaneId | undefined;
}

export const ALPHA_CAMERA_ZOOM_MIN = 0.85;
export const ALPHA_CAMERA_ZOOM_DEFAULT = 1;
export const ALPHA_CAMERA_ZOOM_MAX = 1.55;

export const ALPHA_CAMERA_PAN_BY_SNAP: Readonly<Record<AlphaBattlefieldPanSnap, number>> = {
  opponent: -0.8,
  overview: 0,
  player: 0.65,
};

export function clampAlphaCameraPan(value: number): number {
  return clampAlphaCameraPanY(value);
}

export function clampAlphaCameraPanX(value: number): number {
  return Math.max(-0.35, Math.min(0.35, value));
}

export function clampAlphaCameraPanY(value: number): number {
  return Math.max(-0.8, Math.min(0.65, value));
}

export function clampAlphaCameraZoom(value: number): number {
  return Math.max(ALPHA_CAMERA_ZOOM_MIN, Math.min(ALPHA_CAMERA_ZOOM_MAX, value));
}

export interface CameraPointerPoint {
  readonly x: number;
  readonly y: number;
}

export function cameraPointerDistance(
  first: CameraPointerPoint,
  second: CameraPointerPoint,
): number {
  return Math.hypot(second.x - first.x, second.y - first.y);
}

export function cameraPointerCentre(
  first: CameraPointerPoint,
  second: CameraPointerPoint,
): CameraPointerPoint {
  return { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 };
}

export function alphaCameraZoomFromPinch(
  initialZoom: number,
  initialDistance: number,
  currentDistance: number,
): number {
  if (initialDistance <= 0) return clampAlphaCameraZoom(initialZoom);
  return clampAlphaCameraZoom(initialZoom * (currentDistance / initialDistance));
}

export function alphaCameraZoomFromWheel(currentZoom: number, deltaY: number): number {
  return clampAlphaCameraZoom(currentZoom * Math.exp(-deltaY * 0.0014));
}

export function alphaCameraSnapForPan(value: number): AlphaBattlefieldPanSnap {
  if (value <= -0.3) return "opponent";
  if (value >= 0.28) return "player";
  return "overview";
}

export function alphaCameraMode({
  result,
  inspecting,
  combat,
  attack,
  placement,
  aiTurn,
  selectedCard,
}: {
  readonly result: boolean;
  readonly inspecting: boolean;
  readonly combat: boolean;
  readonly attack: boolean;
  readonly placement: boolean;
  readonly aiTurn: boolean;
  readonly selectedCard: boolean;
}): AlphaBattlefieldCameraMode {
  if (result) return "result";
  if (inspecting) return "inspect";
  if (combat) return "combat";
  if (attack) return "attack";
  if (placement) return "placement";
  if (aiTurn) return "opponent";
  if (selectedCard) return "player";
  return "overview";
}
